/**
 * V9.47 — Sticky banner surfacing global LLM budget-depletion state.
 *
 * Background: When the Emergent Universal Key runs out of credit, chat
 * AI responses stop working. But reminders, task nudges, SMS
 * dispatch, and every non-AI feature keep functioning via
 * deterministic fallbacks in `task_brain`. Without this banner the
 * user would think the whole app is broken.
 *
 * Behaviour:
 *   • Polls `GET /api/llm-status` every 60s (and on mount).
 *   • Renders a sticky cyan/amber banner at the top of the layout
 *     when `depleted: true`. Otherwise renders nothing.
 *   • Text is calm and specific — "AI responses are temporarily
 *     limited. Reminders, tasks, and SMS keep working. Top up the
 *     Universal Key in your Emergent profile."
 *   • Dismissible for the current tab (returns on next reload); the
 *     server-side flag auto-heals when a successful LLM call lands.
 */
import { useEffect, useState } from "react";
import { WarningCircle, X } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;
const DISMISS_STORAGE_KEY = "journey:llm-budget-banner-dismissed-at";

export default function LlmBudgetBanner() {
  const [status, setStatus] = useState(null);
  const [dismissedAt, setDismissedAt] = useState(() => {
    try {
      const v = sessionStorage.getItem(DISMISS_STORAGE_KEY);
      return v ? Number(v) : 0;
    } catch {
      /* sessionStorage unavailable (privacy mode, iframe) — treat as fresh */
      return 0;
    }
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const r = await fetch(`${BACKEND_URL}/api/llm-status`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setStatus(data);
      } catch {
        // Network hiccup — leave the banner alone; next tick will retry.
      }
    }

    fetchStatus();
    const id = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Reset dismissal if depletion re-triggered AFTER the last dismiss.
  useEffect(() => {
    if (!status?.depleted || !status?.since_utc) return;
    const sinceMs = Date.parse(status.since_utc);
    if (dismissedAt && sinceMs > dismissedAt) {
      setDismissedAt(0);
      try {
        sessionStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch {
        /* sessionStorage unavailable — no-op */
      }
    }
  }, [status?.depleted, status?.since_utc, dismissedAt]);

  if (!status?.depleted) return null;
  if (dismissedAt) return null;

  const onDismiss = () => {
    const now = Date.now();
    setDismissedAt(now);
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, String(now));
    } catch {
      /* sessionStorage unavailable — no-op */
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="llm-budget-banner"
      className="sticky top-0 z-40 border-b border-amber-400/30 bg-amber-500/10 backdrop-blur-lg text-amber-100 safe-x"
    >
      <div className="max-w-6xl mx-auto flex items-start gap-3 px-4 py-3">
        <WarningCircle
          size={20}
          weight="fill"
          className="text-amber-300 shrink-0 mt-0.5"
        />
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <div className="font-medium text-amber-50">
            AI responses are temporarily limited
          </div>
          <div className="text-amber-100/85 mt-0.5">
            Reminders, tasks, and SMS keep working with fallback templates.
            To restore full AI chat, top up the{" "}
            <span className="font-semibold text-amber-50">Universal Key</span>{" "}
            in your Emergent profile → <em>Add Balance</em>.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss AI budget banner"
          data-testid="llm-budget-banner-dismiss"
          className="p-1 -m-1 text-amber-200/70 hover:text-amber-100 shrink-0"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
